import { GetHotelBookingQuery, TCancellationPenalties } from '@sim-admin-frontends/data-access';
import { FormInput, FormTextarea } from '@sim-admin-frontends/ui-shared';
import {
  formatISODate,
  DATE_FORMAT_SHORT_WITH_TIME,
  diffInMilliseconds,
} from '@sim-admin-frontends/utils-shared';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

const getCancellationPolicyText = (cancellationPenalties: TCancellationPenalties, t: TFunction) => {
  if (!cancellationPenalties) {
    return '';
  }

  const { freeCancellationBefore, policies } = cancellationPenalties;
  let text = '';

  if (freeCancellationBefore) {
    text += `${t('bookings.freeCancellation', {
      date: formatISODate(freeCancellationBefore, DATE_FORMAT_SHORT_WITH_TIME),
    })} `;
  }

  const policyWithDate = policies
    .filter((policy) => policy.startAt && +policy.amountShow > 0)
    .sort((a, b) => diffInMilliseconds(a.startAt || '', b.startAt || ''))[0];

  if (policyWithDate) {
    text += `${t('bookings.cancelledAfter', {
      date: formatISODate(policyWithDate.startAt || '', DATE_FORMAT_SHORT_WITH_TIME),
      cost: policyWithDate.showAmountWithCommission,
    })} `;
    return text.trim();
  }

  const fallbackPolicy = policies.find((policy) => +policy.amountShow > 0);
  if (fallbackPolicy) {
    text += `${t('bookings.cancellationCost', {
      cost: fallbackPolicy.showAmountWithCommission,
    })} `;
  }

  return text.trim();
};

type Props = {
  room: GetHotelBookingQuery['getHotelBooking']['rooms'][number];
  index: number;
};

const PaymentEditHotelRoom = ({ room, index }: Props) => {
  const { t } = useTranslation();
  const cancellationPenalties = room.room.rate.cancellationPenalties;
  const { guests } = room;

  const cancellationPolicyText = cancellationPenalties
    ? getCancellationPolicyText(cancellationPenalties, t)
    : undefined;

  return (
    <>
      <FormInput
        label={`${t('payments.roomName')} ${index + 1}`}
        value={room.room.roomName}
        readOnly
        disabled
      />
      <FormTextarea
        label={t('payments.cancellationPolicy')}
        value={cancellationPolicyText}
        readOnly
        disabled
      />
      {guests?.map(({ adults, children }) => (
        <FormInput
          key={`${adults}-${children.length}`}
          label={t('payments.guests')}
          value={`${t('payments.adults')}: ${adults}, ${t('payments.children')}: ${
            children.length
          }`}
          readOnly
          disabled
        />
      ))}
    </>
  );
};

export default PaymentEditHotelRoom;
